"use client";

import { useRef } from "react";
import { Dialog } from "radix-ui";
import { ChevronLeft, X } from "lucide-react";

import { useBackClose } from "@/lib/ui/use-back-close";
import { cn } from "@/lib/utils";

/**
 * Folha de baixo (celular): ação ou escolha rápida — resumo da linha,
 * filtros, "+ tarefa neste dia", ações do botão +. Sobe da borda de baixo,
 * raio 12 em cima, régua de 2px, puxador, SEMPRE com botão de fechar
 * visível, e respeita a barra de gestos (safe area).
 *
 * - `description`: linha de apoio visível sob o título.
 * - `onBack`: segundo passo da mesma folha (ex.: "Escolha a obra") — mostra
 *   "‹" à esquerda do título. O voltar do Android faz o mesmo que o "‹"
 *   (sem `onBack`, o voltar fecha a folha — ver lib/ui/back-closes).
 * - `bodyClassName`: troca o respiro do corpo (lista de borda a borda).
 */
export default function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  onBack,
  backLabel = "Voltar",
  children,
  footer,
  className,
  bodyClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  onBack?: () => void;
  backLabel?: string;
  children: React.ReactNode;
  /** Botões fixos no pé da folha. */
  footer?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  useBackClose(contentRef, () => onBack?.(), open && !!onBack);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          ref={contentRef}
          className={cn(
            "pb-safe fixed inset-x-0 bottom-0 z-50 flex max-h-window flex-col rounded-t-xl border-t-2 border-ink bg-ground text-ink shadow-window outline-none",
            className,
          )}
        >
          <span
            aria-hidden
            className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line"
          />
          <header
            className={cn(
              "flex items-center gap-3 border-b border-line py-1 pr-2",
              onBack ? "pl-2" : "pl-5",
            )}
          >
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label={backLabel}
                className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 outline-none hover:bg-panel hover:text-ink focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronLeft className="size-5" />
              </button>
            )}
            <div className="min-w-0 flex-1 py-1.5">
              <Dialog.Title className="truncate text-title-sm font-extrabold tracking-caps uppercase">
                {title}
              </Dialog.Title>
              <Dialog.Description
                className={description ? "truncate text-xs text-ink-2" : "sr-only"}
              >
                {description ?? title}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 outline-none hover:bg-panel hover:text-ink focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-5" />
            </Dialog.Close>
          </header>
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto px-5 py-4",
              bodyClassName,
            )}
          >
            {children}
          </div>
          {footer && (
            <div className="flex gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
